import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Flame, 
  Bookmark,
  Calendar,
  Vote,
  Trash2,
  StickyNote,
  FileText,
  Sparkles,
  ExternalLink,
  Share2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Bill, BillDisplayContext, WorkspacePermissions, BILL_POSITIONS } from '../../types/bill';
import { getPositionColorClasses } from '../../utils/positionColors';
import { hasUpcomingCalendarEvent } from '../../utils/calendarUtils';
import Chip, { getStatusVariant } from '../Chip';
import CalendarEventDisplay from '../shared/CalendarEventDisplay';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';
import SponsorDisplay from '../SponsorDisplay';
import { AmendmentCell } from '../shared/AmendmentCell';
import { useEntityNotes, useEntityNoteCounts, noteCountFor } from '../../hooks/useNotesV2';
import { ViewNotesModal } from '../notes';
import { formatDate } from '../../utils/dateUtils';

// Card notes button component - for workspace context
interface CardNotesButtonProps {
  entityType: string;
  entityId: string;
  entityDisplayName: string;
  workspaceId: string;
  /** Count from the list-level batch (#1179); notes are fetched only when the modal opens. */
  batchCount?: number | 'loading';
}

const CardNotesButton: React.FC<CardNotesButtonProps> = ({ entityType, entityId, entityDisplayName, workspaceId, batchCount }) => {
  const [showModal, setShowModal] = useState(false);
  const { notes, isLoading } = useEntityNotes(entityType, entityId, workspaceId, 100, batchCount === undefined || showModal);
  const noteCount = typeof batchCount === 'number' ? batchCount : notes.length;
  const pending = batchCount === 'loading' || (batchCount === undefined && isLoading);

  if (pending) {
    return (
      <button
        onClick={(e) => e.stopPropagation()}
        className="p-1 text-text-tertiary dark:text-text-dark-tertiary cursor-wait"
        disabled
      >
        <StickyNote className="w-4 h-4" />
      </button>
    );
  }

  return (
    <>
      <button 
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
        }}
        className={`p-1 transition-colors ${
          noteCount > 0
            ? 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
            : 'text-text-tertiary dark:text-text-dark-tertiary hover:text-text-secondary dark:hover:text-text-dark-secondary'
        }`}
        title={noteCount > 0 ? `View ${noteCount} note${noteCount === 1 ? '' : 's'}` : 'Add a note'}
      >
        <StickyNote className="w-4 h-4" />
      </button>
      {showModal && (
        <ViewNotesModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          notes={notes.map(note => ({
            id: note.id,
            title: note.title,
            content: note.content,
            created_at: note.created_at,
            updated_at: note.updated_at,
            include_in_reports: note.include_in_reports,
            author_name: note.author_name || 'Unknown',
            tags: note.tags || [],
            parent_note_id: note.parent_note_id,
            reply_count: note.reply_count || 0
          }))}
          entityType={entityType}
          entityId={entityId}
          entityDisplayName={entityDisplayName}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
};

interface BillCardsProps {
  bills: Bill[];
  context: BillDisplayContext;
  workspaceId?: string;
  permissions?: WorkspacePermissions;
  selectedBills?: Set<string>;
  trackedBillIds?: Set<string>;
  // Display options
  showCheckboxes?: boolean;
  showCustomFields?: boolean;
  showNotesButton?: boolean;
  // Report mode props
  reportMode?: boolean;
  showCalendarEvents?: boolean;
  notesDisplayMode?: 'inline' | 'badge' | 'none';
  // Callbacks
  onBillSelection?: (billId: string, selected: boolean) => void;
  onPositionUpdate?: (billId: string, position: string) => void;
  onToggleHot?: (billId: string, isHot: boolean) => void;
  onRemoveBill?: (billId: string) => void;
  onBookmark?: (billId: string, billNumber: string, billTitle: string) => void;
  onVotePredictions?: (bill: Bill) => void;
}

const BillCards: React.FC<BillCardsProps> = ({
  bills,
  context,
  workspaceId,
  permissions,
  selectedBills = new Set(),
  trackedBillIds = new Set(),
  showCheckboxes = false,
  showCustomFields = false,
  showNotesButton = false,
  reportMode = false,
  showCalendarEvents = false,
  notesDisplayMode = 'badge',
  onBillSelection,
  onPositionUpdate,
  onToggleHot,
  onRemoveBill,
  onBookmark,
  onVotePredictions
}) => {
  const navigate = useNavigate();
  // One request for every card's note count instead of one per card (#1179).
  const noteCounts = useEntityNoteCounts(
    'bill',
    bills.map((b) => b.id),
    workspaceId,
    context === 'workspace' && showNotesButton && !!workspaceId && !reportMode
  );


  // Format committee name
  const formatCommitteeName = (name: string | undefined | null) => {
    if (!name) return 'Unknown';
    // Remove chamber prefix if present
    const formatted = name
      .replace(/^(House|Senate)\s+Committee\s+on\s+/i, '')
      .replace(/^(House|Senate)\s+/i, '');
    return formatted;
  };

  // Check if calendar event is upcoming (not in the past)
  const isUpcomingEvent = (event: any) => {
    if (!event) return false;
    const eventDate = event.reading_date || event.date;
    if (!eventDate) return false;
    return new Date(eventDate) >= new Date(new Date().toDateString());
  };

  // Navigation handler
  const handleBillClick = (billId: string) => {
    const url = workspaceId 
      ? `/legislation/${billId}?workspace=${workspaceId}&returnTo=workspace`
      : `/legislation/${billId}`;
    navigate(url);
  };

  // Subjects, derived exactly as the table's Subjects column does.
  const getSubjects = (bill: Bill): string[] =>
    bill.categories && bill.categories.length > 0
      ? bill.categories
      : Array.isArray(bill.subject)
        ? bill.subject
        : bill.subject
          ? [bill.subject]
          : [];

  // Same destination as the table's Amended column.
  const handleAmendmentClick = (billId: string) => {
    const url = workspaceId
      ? `/legislation/${billId}?tab=amendments&workspace=${workspaceId}&returnTo=workspace`
      : `/legislation/${billId}?tab=amendments`;
    navigate(url);
  };

  // Get House committees
  const getHouseCommittees = (bill: Bill) => {
    if (!bill.committees || bill.committees.length === 0) return null;
    const houseCommittees = bill.committees.filter(c => c.chamber?.toLowerCase() === 'house');
    if (houseCommittees.length === 0) return null;
    return houseCommittees.map(c => formatCommitteeName(c.name)).join(', ');
  };

  // Get Senate committees
  const getSenateCommittees = (bill: Bill) => {
    if (!bill.committees || bill.committees.length === 0) return null;
    const senateCommittees = bill.committees.filter(c => c.chamber?.toLowerCase() === 'senate');
    if (senateCommittees.length === 0) return null;
    return senateCommittees.map(c => formatCommitteeName(c.name)).join(', ');
  };

  if (bills.length === 0) {
    return (
      <div className="card p-8 text-center">
        <p className="text-text-secondary dark:text-text-dark-secondary">
          No bills found matching your filters.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {bills.map(bill => {
        const isTracked = trackedBillIds.has(bill.id);
        const isSelected = selectedBills.has(bill.id) || selectedBills.has(bill.id.toString());
        const hasNotes = notesDisplayMode === 'badge' && (bill as any).notes?.length > 0;
        const hasCustomFields = bill.customFieldCount && bill.customFieldCount > 0;

        return (
          <div key={bill.id} className="relative">
            {/* Checkbox for selection */}
            {showCheckboxes && onBillSelection && (
              <div className="absolute top-3 left-3 z-10">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onBillSelection(bill.id.toString(), e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 text-primary-600 bg-background-tertiary border-border-light-secondary rounded focus:ring-primary-500 focus:ring-2"
                />
              </div>
            )}
            
            <div 
              className={`card p-4 hover:shadow-lg transition-shadow ${
                showCheckboxes ? 'pt-8' : ''
              } ${
                isSelected ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20' : ''
              }`}
            >
              {/* Header Row */}
              <div className="mb-3">
                {/* Bill Number + pop-out/hot on left, action icons on right */}
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleBillClick(bill.id)}
                      className="font-bold text-base text-primary-700 dark:text-primary-400 hover:underline"
                    >
                      {bill.billNumber}
                    </button>
                    {/* Open in New Tab - next to bill number */}
                    <a
                      href={workspaceId 
                        ? `/legislation/${bill.id}?workspace=${workspaceId}&returnTo=workspace&popup=true`
                        : `/legislation/${bill.id}?popup=true`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 transition-colors flex-shrink-0"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    {/* Hot Bill Toggle - next to bill number */}
                    {context === 'workspace' && permissions?.canEdit && onToggleHot && (
                      <button 
                        className="p-1 text-error-600 dark:text-error-400 hover:scale-110 transition-transform flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleHot(bill.id, !bill.isHot);
                        }}
                        title={bill.isHot ? "Remove from hot bills" : "Mark as hot bill"}
                      >
                        <Flame className="w-4 h-4" fill={bill.isHot ? 'currentColor' : 'none'} />
                      </button>
                    )}
                    {bill.isHot && reportMode && (
                      <Flame className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" fill="currentColor" />
                    )}
                  </div>
                  
                  {/* Action Icons Row - same order as BillTable: Notes, Fields, Vote, Share, Bookmark/Remove */}
                  {!reportMode && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Notes Button - workspace only */}
                      {context === 'workspace' && showNotesButton && workspaceId && (
                        <CardNotesButton
                          entityType="bill"
                          entityId={bill.id}
                          entityDisplayName={`${bill.billNumber || 'Unknown'} - ${bill.title || 'No title'}`}
                          workspaceId={workspaceId}
                          batchCount={noteCountFor(noteCounts, bill.id)}
                        />
                      )}
                      
                      {/* Custom Field Badge - workspace only */}
                      {context === 'workspace' && workspaceId && (
                        <CustomFieldBadge
                          entityType="bill"
                          entityId={bill.id}
                          entityDisplayName={`${bill.billNumber}: ${bill.title}`}
                          workspaceId={workspaceId}
                          count={bill.customFieldCount}
                          canEdit={permissions?.canEdit || false}
                        />
                      )}

                      {/* Bookmark - Legislation context */}
                      {context === 'legislation' && onBookmark && (
                        <button 
                          className={`p-1 transition-colors ${
                            isTracked 
                              ? 'text-primary-600 hover:text-primary-700' 
                              : 'text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onBookmark(bill.id, bill.billNumber, bill.title);
                          }}
                          title={isTracked ? "Manage bookmarks" : "Bookmark to workspace"}
                        >
                          <Bookmark className="w-4 h-4" fill={isTracked ? 'currentColor' : 'none'} />
                        </button>
                      )}
                      
                      {/* Vote Predictions */}
                      {onVotePredictions && (
                        <button 
                          className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onVotePredictions(bill);
                          }}
                          title="Vote predictions"
                        >
                          <Vote className="w-4 h-4" />
                        </button>
                      )}

                      {/* Share */}
                      <button
                        className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          const publicUrl = `${window.location.origin}/public/bills/${encodeURIComponent(bill.billNumber)}`;
                          navigator.clipboard.writeText(publicUrl).then(() => {
                            toast.success('Public link copied to clipboard');
                          }).catch(() => {
                            const textArea = document.createElement('textarea');
                            textArea.value = publicUrl;
                            document.body.appendChild(textArea);
                            textArea.select();
                            document.execCommand('copy');
                            document.body.removeChild(textArea);
                            toast.success('Public link copied to clipboard');
                          });
                        }}
                        title="Copy public link to bill"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                      
                      {/* Remove - Workspace context */}
                      {context === 'workspace' && permissions?.canEdit && onRemoveBill && (
                        <button 
                          className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-error-600 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveBill(bill.id);
                          }}
                          title="Stop tracking"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Status Chip and Position */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Chip 
                    variant={getStatusVariant(bill.status)} 
                    label={bill.status} 
                    size="sm" 
                  />
                  {/* canManage, not canEdit — see BillTable's position cell. */}
                  {(bill.billPosition || (permissions?.canManage && onPositionUpdate && !reportMode)) && (
                    permissions?.canManage && onPositionUpdate && !reportMode ? (
                      <select
                        value={bill.billPosition || 'Pending'}
                        onChange={(e) => {
                          e.stopPropagation();
                          onPositionUpdate(bill.id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className={`text-xs border border-border-light dark:border-border-dark rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium cursor-pointer ${getPositionColorClasses(bill.billPosition || 'Pending')}`}
                      >
                        {BILL_POSITIONS.map(position => (
                          <option 
                            key={position.value} 
                            value={position.value}
                            className="bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary"
                          >
                            {position.label}
                          </option>
                        ))}
                      </select>
                    ) : bill.billPosition ? (
                      <span className={`text-xs px-2 py-1 rounded ${getPositionColorClasses(bill.billPosition)}`}>
                        {bill.billPosition}
                      </span>
                    ) : null
                  )}
                </div>
              </div>

            {/* Title with AI Summary indicator */}
            <div className="mb-3">
              <h3 
                className="text-sm text-text-primary dark:text-text-dark-primary font-medium line-clamp-2 cursor-pointer hover:text-primary-700 dark:hover:text-primary-400"
                onClick={() => handleBillClick(bill.id)}
              >
                {bill.title}
              </h3>
              
              {/* AI Summary Tooltip */}
              {bill.aiShortSummary && (
                <div className="group relative inline-flex items-center gap-1 mt-1">
                  <Sparkles className="w-3.5 h-3.5 text-primary-500" />
                  <span className="text-xs text-primary-600 dark:text-primary-400">AI Summary</span>
                  
                  {/* Tooltip */}
                  <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block w-64 p-3 bg-white dark:bg-background-dark-secondary rounded-lg shadow-lg border border-border-light dark:border-border-dark">
                    <div className="flex items-center gap-1 mb-2">
                      <Sparkles className="w-3 h-3 text-primary-500" />
                      <span className="text-xs font-medium text-text-primary dark:text-text-dark-primary">AI Summary</span>
                    </div>
                    <p className="text-xs text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                      {bill.aiShortSummary}
                    </p>
                    <p className="text-[10px] text-text-tertiary dark:text-text-dark-tertiary mt-2 italic">
                      Click bill for full details
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Description Box */}
            {bill.concerning && (
              <div className="mb-3 p-3 bg-white dark:bg-background-dark-secondary border border-border-light dark:border-border-dark rounded-lg">
                <div className="text-xs font-semibold text-text-primary dark:text-text-dark-primary mb-1">Description</div>
                <p className="text-xs text-text-secondary dark:text-text-dark-secondary line-clamp-3">
                  {bill.concerning}
                </p>
              </div>
            )}

            {/* Meta Information — field parity with BillTable (2026-08-29):
                same sponsors component, subjects, and Amended data the list shows. */}
            <div className="text-xs text-text-secondary dark:text-text-dark-secondary space-y-1 mb-3">
              <div>
                <span className="font-medium">Sponsors:</span>
                <SponsorDisplay sponsors={bill.sponsors} billNumber={bill.billNumber} />
              </div>

              {getSubjects(bill).length > 0 && (
                <div><span className="font-medium">Subjects:</span> {getSubjects(bill).join(', ')}</div>
              )}

              {/* House/Senate Committee Breakdown */}
              {getHouseCommittees(bill) && (
                <div><span className="font-medium">House:</span> {getHouseCommittees(bill)}</div>
              )}
              {getSenateCommittees(bill) && (
                <div><span className="font-medium">Senate:</span> {getSenateCommittees(bill)}</div>
              )}

              <div><span className="font-medium">Updated:</span> {formatDate(bill.lastActivity)}</div>
              {bill.introducedDate && (
                <div><span className="font-medium">Introduced:</span> {formatDate(bill.introducedDate)}</div>
              )}

              {bill.lastAmendment && (
                <div className="flex items-center gap-1">
                  <span className="font-medium">Amended:</span>
                  <AmendmentCell
                    displayText={formatDate(bill.lastAmendment.date)}
                    hasAmendments
                    onClick={(e) => { e.stopPropagation(); handleAmendmentClick(bill.id); }}
                  />
                </div>
              )}
              
              {/* Next Calendar Event - Highlighted box (only show if event is upcoming) */}
              {bill.nextCalendarEvent && isUpcomingEvent(bill.nextCalendarEvent) && (
                <div className="mt-3 p-2 bg-purple-50 dark:bg-purple-900/20 rounded border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-1 mb-1">
                    <Calendar className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                    <span className="text-xs font-medium text-purple-900 dark:text-purple-300">Calendar Date</span>
                  </div>
                  <CalendarEventDisplay
                    event={bill.nextCalendarEvent}
                    billNumber={bill.billNumber}
                    billTitle={bill.title}
                    variant="inline"
                    showIcon={false}
                    showLabel={true}
                    showAddToCalendar={true}
                    className="text-xs"
                  />
                </div>
              )}
            </div>

            {/* Calendar Events (for hot bills) */}
            {showCalendarEvents && hasUpcomingCalendarEvent(bill.calendarEvents) && (
              <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-1 mb-2">
                  <Calendar className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  <span className="text-xs font-medium text-blue-900 dark:text-blue-300">Calendar Date</span>
                </div>
                <CalendarEventDisplay
                  events={bill.calendarEvents}
                  billNumber={bill.billNumber}
                  billTitle={bill.title}
                  variant="list"
                  showIcon={false}
                  showLabel={false}
                  maxDisplay={2}
                  showMoreCount={true}
                  showAddToCalendar={true}
                  showTitle={true}
                />
              </div>
            )}

            {/* Notes and Custom Fields - Simple text indicators (for report context without interactive buttons) */}
            {!showNotesButton && !showCustomFields && (hasNotes || hasCustomFields) && (
              <div className="flex items-center gap-2 mb-3 text-xs">
                {hasNotes && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded">
                    <StickyNote className="w-3 h-3" />
                    {(bill as any).notes.length} note{(bill as any).notes.length !== 1 ? 's' : ''}
                  </span>
                )}
                {hasCustomFields && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded">
                    <FileText className="w-3 h-3" />
                    {bill.customFieldCount} field{bill.customFieldCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BillCards;
